/**
 * Website Make changes ↔ the Workbench that shows the site in an iframe
 * (docs/website_make_changes.md). The mode runs INSIDE the site preview; the
 * Workbench owns what is outside it: the full-screen layer the mode opens in
 * (like a transaction screen's Make changes), the Ask-AI panel, the tree.
 *
 * Same-origin `postMessage` only, both ways checked: the host accepts a message
 * from its own frame's window and origin, the frame posts to its own origin.
 */

export const SITE_EDIT_MESSAGE = 'twasta:site-edit'

export type SiteEditEvent =
  /** The mode opened (`on`) or closed; `changes` = how many were stored. */
  | { event: 'mode'; on: boolean; changes?: number }
  /** The mode asks to cover the whole window, or to go back into its pane. */
  | { event: 'fullscreen'; on: boolean }
  /** ✦ Ask AI to change: pre-fill the host's Ask-AI panel (never sends). */
  | { event: 'ask-ai'; text: string }
  /** The website's pages or settings changed (a page added, the menu…). */
  | { event: 'site-changed' }

export type SiteEditMessage = SiteEditEvent & { type: typeof SITE_EDIT_MESSAGE }

/** The site preview is inside the Workbench (or another same-origin host). */
export function inHostFrame(): boolean {
  try {
    return window.parent !== window && window.parent.location.origin === window.location.origin
  } catch {
    return false   // a cross-origin parent: not ours to talk to
  }
}

export function postToHost(msg: SiteEditEvent): void {
  if (!inHostFrame()) return
  try {
    window.parent.postMessage({ type: SITE_EDIT_MESSAGE, ...msg }, window.location.origin)
  } catch (e) {
    console.warn('[site-edit] could not reach the Workbench', e)
  }
}

/** Reads a message from `frame`; null when it is not one of ours. */
export function readSiteEditMessage(e: MessageEvent, frame: HTMLIFrameElement | null): SiteEditMessage | null {
  if (!frame || e.source !== frame.contentWindow || e.origin !== window.location.origin) return null
  const d = e.data as Partial<SiteEditMessage> | null
  return d && typeof d === 'object' && d.type === SITE_EDIT_MESSAGE ? d as SiteEditMessage : null
}
